import { useEffect, useState } from "react";
import moment from "moment";
import { FieldValues, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation, withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import CustomForm from "@components/shared/CustomForm/CustomForm";
import Loader from "@components/shared/Loader/Loader";
import { CONSTANTS } from "@constants/index";
import { CONTACT_TYPE } from "@constants/register";
import useRegister from "@hooks/useRegister/useRegister";
import { ErrorData, UserFormData } from "@models/components/auth.model";
import { ProfileProps, updateProfile } from "@models/components/user.model";
import handleResponse from "@services/apiConfig/api.response.service";
import API_SERVICES from "@services/index";
import userSlice from "@slices/user.slice";
import { RootState } from "@store/index";
import commonUtils from "@utils/common.utils";

const ProfileInfoEditComponent = (props: ProfileProps) => {
  const [isFetching, setIsFetching] = useState(false);

  const { selectedLanguage } = useSelector((state: RootState) => state.user);
  const { closeForm, data, getUserProfile } = props;

  const dispatch = useDispatch();

  const initialState: FieldValues = {
    city: data.profile.city,
    companyName: data.profile?.companyName,
    contactType: data.profile.contactType,
    country: data.profile.country,
    dob: data.profile.dob
      ? moment(data.profile.dob).format(
          CONSTANTS.DATE_FORMATS.DAY_MONTH_YEAR_FORMAT
        )
      : "",
    email: data.email,
    firstName: data.profile.firstName,
    gender: data.profile.gender,
    id: data.profile.id,
    language: selectedLanguage || data?.profile?.language,
    lastName: data.profile.lastName,
    registrationDate: data.profile.registrationDate,
    salutation: data.profile.salutation,
    street: data.profile.street,
    telephoneNumber: data.profile.telephoneNumber,
    zipcode: data.profile.zipcode,
  };

  const {
    control,
    formState: { errors, isValid },
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
  } = useForm({
    defaultValues: initialState,
    mode: "onTouched",
    values: initialState,
  });

  const { t } = useTranslation();
  const {
    activeFields,
    countries,
    isFetching: isLoading,
    registerOptions,
    updateFields,
  } = useRegister(watch("contactType"));

  useEffect(() => {
    updateFields(watch("contactType"));
    if (selectedLanguage) setValue("language", selectedLanguage);
  }, [selectedLanguage, watch("contactType")]);

  useEffect(() => {
    reset(initialState);
  }, [countries, reset]);

  useEffect(() => {
    setIsFetching(isLoading);
  }, [isLoading]);

  const submitForm: SubmitHandler<UserFormData> = async data => {
    Object.keys(data).forEach(k => {
      const key = k as keyof UserFormData;
      if (typeof data[key] === "string") {
        data[key] = data[key]!.trim();
      }
    });

    if (isValid) {
      setIsFetching(true);
      const commonData = {
        city: data.city,
        contactType: data.contactType,
        country: data.country,
        dob: data?.dob
          ? moment(
              data.dob,
              CONSTANTS.DATE_FORMATS.DAY_MONTH_YEAR_FORMAT
            ).format(CONSTANTS.DATE_FORMATS.YEAR_MONTH_DAY_HYPHEN_FORMAT)
          : "",
        firstName: data.firstName,
        gender: data.gender,
        language: data.language || "",
        lastName: data.lastName,
        salutation: data.salutation,
        street: data.street || "",
        telephoneNumber: data.telephoneNumber || "",
        zipcode: data.zipcode || "",
      };

      const companyWithoutMainContact = {
        city: data.city,
        companyName: data.companyName || "",
        contactType: data.contactType,
        country: data.country,
        language: data.language || "",
        street: data.street || "",
        telephoneNumber: data.telephoneNumber || "",
        zipcode: data.zipcode || "",
      };

      const apiRequestBody = {
        [CONTACT_TYPE.COMPANY_WITH_CONTACT]: {
          ...commonData,
          companyName: data.companyName || "",
        },
        [CONTACT_TYPE.COMPANY_WITHOUT_CONTACT]: companyWithoutMainContact,
        [CONTACT_TYPE.SINGLE]: commonData,
      };

      const response = await API_SERVICES.USER.UPDATE_USER_PROFILE(
        apiRequestBody[watch("contactType")] as updateProfile
      );
      const responseData = handleResponse(response);
      setIsFetching(false);

      if (responseData?.ok) {
        dispatch(
          userSlice.actions.userSelectedLanguage({
            active_language: data.language || "",
          })
        );
        if (getUserProfile) getUserProfile();
        commonUtils.toastService(
          t("userProfile.profileUpdated"),
          CONSTANTS.TOAST_TYPE.SUCCESS
        );
        if (closeForm) closeForm();
      }
    }
  };

  const formDataProps = {
    errors: errors as ErrorData,
    handleSubmit: handleSubmit(submitForm as SubmitHandler<FieldValues>),
    isSubmitButtonDisabled: isFetching,
    register: register,
    setValue: setValue,
  };

  return (
    <div className="row" data-testid="profile-info-edit">
      {isFetching && <Loader isLoading={true} />}
      <div className="col-sm-12">
        <div className="fg-profile-edit-section">
          <CustomForm
            activeFields={activeFields}
            closeForm={closeForm}
            control={control}
            formData={formDataProps}
            isEdit={true}
            registerOptions={registerOptions}
          />
        </div>
      </div>
    </div>
  );
};

const ProfileInfoEdit = withTranslation()(ProfileInfoEditComponent);

export default ProfileInfoEdit;
